import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@pantry/ui';
import { useCancelInvitation, type InvitationWithInviter } from '@pantry/api-client';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CancelInvitationDialogProps {
  householdId: string;
  invitation: InvitationWithInviter;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CancelInvitationDialog({
  householdId,
  invitation,
  open,
  onOpenChange,
}: CancelInvitationDialogProps) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);

  const cancelInvitation = useCancelInvitation(householdId);

  const handleCancel = async () => {
    setError(null);

    try {
      await cancelInvitation.mutateAsync(invitation.id);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to cancel invitation`;
      setError(message);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
              <XMarkIcon className="h-5 w-5 text-destructive" />
            </div>
            <Trans>Cancel Invitation</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">
            <Trans>
              Are you sure you want to cancel the invitation to{' '}
              <span className="font-medium text-foreground">{invitation.email}</span>? They will no
              longer be able to join using the invitation link.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
            {error}
          </div>
        )}
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel disabled={cancelInvitation.isPending} className="rounded-xl">
            <Trans>Keep Invitation</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleCancel}
            disabled={cancelInvitation.isPending}
            className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
          >
            {cancelInvitation.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <Trans>Cancelling...</Trans>
              </div>
            ) : (
              <Trans>Cancel Invitation</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
